/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, {
	FunctionComponent,
	MouseEvent,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { BEM, SYSTEM_COLOR } from "@adapptio/lib-components";
import { RESOURCE_TYPES } from "@adapptio/lib-core";

import { IAppInfo } from "../../common/AppInfo";
import { DeploymentService } from "../../deployment/DeploymentService";
import { ResourceService } from "../../resources/ResourceService";
import { ViewService } from "../../view/ViewService";
import { useEscKey } from "../hooks/useEscKey";
import { AppMenu, IAppMenuProps } from "./AppMenu";
import { Icon, ICON_SIZE } from "./Icon";
import { MainToolbarTabList } from "./MainToolbarTabs";
import { Badge } from "./Badge";
import { DOC_ERROR_SEVERITY } from "@adapptio/lib-blueprint";
import { ToolbarCommandButton } from "./Toolbar/ToolbarCommandButton";
import { Toolbar, TOOLBAR_STYLE } from "./Toolbar/Toolbar";
import { ACTION_BUTTON_STYLE } from "./ActionButton";
import { useStateWithEvent } from "../hooks/useStateWithEvent";
import { useSimpleEvent } from "../hooks";
import { ProductSupportService } from "../../productSupport/ProductSupportService";
import { ScmService } from "../../scm/ScmService";

interface IAppButtonProps {
	appInfo: IAppInfo;
	resourceService: ResourceService;
	viewService: ViewService;
	scmService: ScmService;
}

const AppButton: FunctionComponent<IAppButtonProps> = ({ appInfo, resourceService, viewService, scmService }) => {
	const [ isMenuOpened, setIsMenuOpened ] = useState(false);
	const [ menuForceOpened, setMenuForceOpened ] = useState(false);
	const isMounted = useRef(false);
	const toggleTimer = useRef<number>(null);

	const baseClass = BEM("edt-main-toolbar", "app-menu", {
		opened: isMenuOpened || menuForceOpened
	});

	const scmInfo = useStateWithEvent(scmService.onScmInfoChange, () => scmService.getScmInfo());

	useEffect(() => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);

	// Close with ESC key
	useEscKey(() => {
		setIsMenuOpened(false);
	}, isMenuOpened);

	const handleMouseEnter = useCallback(() => {
		if (toggleTimer.current) {
			clearTimeout(toggleTimer.current);
		}
	}, []);

	const handleMouseLeave = useCallback(() => {
		if (toggleTimer.current) {
			clearTimeout(toggleTimer.current);
		}
	}, []);

	const handleMenuToggle = useCallback(
		(ev: MouseEvent<HTMLButtonElement>) => {
			ev.stopPropagation();

			if (menuForceOpened) {
				return;
			}

			if (toggleTimer.current) {
				clearTimeout(toggleTimer.current);
			}

			setIsMenuOpened((x) => !x);
		},
		[ menuForceOpened ]
	);

	const handleMenuInteraction = useCallback<IAppMenuProps["onInteraction"]>(({ node } = {}) => {
		if (node?.resourceType === RESOURCE_TYPES.DIRECTORY) {
			return;
		}

		if (toggleTimer.current) {
			clearTimeout(toggleTimer.current);
		}

		setIsMenuOpened(false);
	}, []);

	const handleMenuOverlayClick = useCallback<React.MouseEventHandler>(() => {
		handleMenuInteraction({});
	}, []);

	useSimpleEvent(viewService.onViewListChanged, () => {
		if (viewService.getViewInstanceList()?.length === 0) {
			setMenuForceOpened(true);
		} else {
			setMenuForceOpened(false);
			setIsMenuOpened(false);
		}
	});

	useEffect(() => {
		setMenuForceOpened(viewService.getViewInstanceList()?.length === 0);
	}, []);

	return (
		<div
			className={baseClass.toClassName()}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<button className={baseClass.element("app-menu-button").toClassName()} onClick={handleMenuToggle}>
				<Icon name="mdi/menu" size={ICON_SIZE.MEDIUM} />
				<span className={baseClass.element("app-menu-button-container").toClassName()}>
					<span className={baseClass.element("app-menu-button-appname").toClassName()}>
						{appInfo.name}
					</span>
					{scmInfo && (
						<span className={baseClass.element("app-menu-button-branchname").toClassName()}>
							<Icon name="mdi/source-branch" size={ICON_SIZE.XXSMALL} /> {scmInfo?.workingBranch}
						</span>
					)}
				</span>
				<Icon
					className={baseClass.element("app-menu-button-arrow").toClassName()}
					name="mdi/menu-down"
					size={ICON_SIZE.MEDIUM}
				/>
				<span className={baseClass.element("app-menu-button-corner", "right").toClassName()} />
			</button>
			<div className={baseClass.element("app-menu-wrapper").toClassName()}>
				{isMenuOpened && (
					<div
						className={baseClass.element("app-menu-overlay").toClassName()}
						onClick={handleMenuOverlayClick}
					/>
				)}
				<div className={baseClass.element("app-menu-container").toClassName()}>
					<AppMenu
						resourceService={resourceService}
						isActive={isMenuOpened || menuForceOpened}
						onInteraction={handleMenuInteraction}
					/>
				</div>
			</div>
		</div>
	);
};

/**
 * Component props
 */
export interface IMainToolbarProps {
	appInfo: IAppInfo;
	resourceService: ResourceService;
	scmService: ScmService;
	deploymentService: DeploymentService;
	viewService: ViewService;
	productSupportService: ProductSupportService;
}

export const MainToolbar: FunctionComponent<IMainToolbarProps> = ({
	appInfo,
	resourceService,
	deploymentService,
	scmService,
	viewService,
	productSupportService,
}) => {
	const baseClass = BEM("edt-main-toolbar");
	baseClass.add("edt-base");

	const deploymentStatus = useStateWithEvent(deploymentService?.onDeploymentListChange, () =>
		deploymentService?.isDeployInProgress()
	);

	const errorsCount = useStateWithEvent(deploymentService?.onErrorsChanged, () =>
		deploymentService?.getErrorsSeverityCount()
	);

	const canSaveAll = useStateWithEvent(viewService.onViewInfoChanged, () => viewService.canSaveAllViews(), [
		viewService
	]);
	const isSavingAll = useStateWithEvent(viewService.onSavingChange, () => viewService.isSaveInProgress(), [
		viewService
	]);

	const totalDisplayedErrorsCount =
		errorsCount[DOC_ERROR_SEVERITY.ERROR] + errorsCount[DOC_ERROR_SEVERITY.WARNING];
	const hasFatalErrors = errorsCount[DOC_ERROR_SEVERITY.ERROR] > 0;

	return (
		<div className={baseClass.toClassName()}>
			<AppButton
				appInfo={appInfo}
				resourceService={resourceService}
				viewService={viewService}
				scmService={scmService}
			/>
			<MainToolbarTabList viewService={viewService} />
			<Toolbar style={TOOLBAR_STYLE.STANDALONE}>
				<ToolbarCommandButton
					icon="mdi/content-save-all"
					commandId="views.saveAll"
					disabled={!canSaveAll}
					isLoading={isSavingAll}
					tooltip="Save All"
				/>
			</Toolbar>
			<Toolbar style={TOOLBAR_STYLE.STANDALONE}>
				<Badge
					content={totalDisplayedErrorsCount > 0 ? totalDisplayedErrorsCount : null}
					color={hasFatalErrors ? SYSTEM_COLOR.ERROR : SYSTEM_COLOR.WARNING}
				>
					<ToolbarCommandButton icon="mdi/alert" tooltip="Errors" commandId="envErrors.open" />
				</Badge>
			</Toolbar>
			<Toolbar style={TOOLBAR_STYLE.STANDALONE}>
				<ToolbarCommandButton
					icon="mdi/eye"
					tooltip="Preview"
					// style={ACTION_BUTTON_STYLE.PRIMARY}
					commandId="preview.open"
				/>
			</Toolbar>

			<Toolbar style={TOOLBAR_STYLE.STANDALONE}>
				<ToolbarCommandButton
					icon="mdi/rocket-launch"
					label="Release"
					style={ACTION_BUTTON_STYLE.PRIMARY}
					commandId="release.openDialog"
				/>

				{/* XTODO: Replace this with Releases feature */}
				{/* <ToolbarCommandButton
					icon="mdi/cloud-upload"
					label={
						deploymentStatus?.status === DeploymentStatus.Terminating ||
						deploymentStatus?.status === DeploymentStatus.Initializing ||
						deploymentStatus?.status === DeploymentStatus.Deploying
							? deploymentStatus.status
							: "Deploy"
					}
					style={ACTION_BUTTON_STYLE.PRIMARY}
					commandId="deployment.openDialog"
					isLoading={
						deploymentStatus?.status === DeploymentStatus.Terminating ||
						deploymentStatus?.status === DeploymentStatus.Initializing ||
						deploymentStatus?.status === DeploymentStatus.Deploying
					}
				/> */}
			</Toolbar>

			{productSupportService.hasBugReportLink() && (
				<Toolbar style={TOOLBAR_STYLE.STANDALONE}>
					<ToolbarCommandButton
						icon="mdi/bug-outline"
						tooltip="Report a bug"
						commandId="bugReport.send"
					/>
				</Toolbar>
			)}
		</div>
	);
};
